import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Profile } from '@/types/supabase';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface UserListProps {
  members: Profile[];
  onlineUsers: Set<string>;
}

const UserList = ({ members, onlineUsers }: UserListProps) => {
  const sortedMembers = [...members].sort((a, b) => {
    const aOnline = onlineUsers.has(a.id);
    const bOnline = onlineUsers.has(b.id);
    if (aOnline && !bOnline) return -1;
    if (!aOnline && bOnline) return 1;
    return (a.full_name || a.username || '').localeCompare(b.full_name || b.username || '');
  });

  return (
    <aside className="bg-background border-l flex flex-col flex-shrink-0 h-full p-4">
      <h2 className="text-sm font-semibold text-muted-foreground px-2 mb-2 uppercase">
        Members—{sortedMembers.length}
      </h2>
      <ScrollArea className="flex-1">
        <div className="space-y-1">
          {sortedMembers.map((member) => (
            <div key={member.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent">
              <div className="relative">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={member.avatar_url || undefined} alt={member.full_name || member.username || 'User'} />
                  <AvatarFallback>{member.full_name?.charAt(0) || member.username?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <span
                  className={cn(
                    'absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full border-2 border-background',
                    onlineUsers.has(member.id) ? 'bg-green-500' : 'bg-gray-400'
                  )}
                  title={onlineUsers.has(member.id) ? 'Online' : 'Offline'}
                />
              </div>
              <span className="text-sm font-medium truncate">{member.full_name || member.username}</span>
            </div>
          ))}
        </div>
      </ScrollArea>
    </aside>
  );
};

export default UserList;